import _, { includes } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Delete from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableRow from '@mui/material/TableRow';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import addCommas from '../../utils/functions/commas';
import { Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { exportBillPayments, useTransactionForPaymentQuery } from '../../services/paymentTransaction';
import SortableTableLabel from '../../components/Table/SortableTableLabel';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  p: 4,
};

function BillPayment() {
  const { t } = useTranslation();
  const columns = [
    {
      id: 'payment_transaction_no',
      label: t('app.transactionRefNo'),
      minWidth: 100,
    },
    {
      id: 'customer_name',
      label: t('app.customerName'),
      minWidth: 100,
      align: 'center',
    },
    { id: 'payer', label: t('app.username'), minWidth: 100, align: 'center' },
    {
      id: 'transaction_date',
      label: t('app.transactionDate'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'payment_type',
      label: t('app.paymentMethod'),
      minWidth: 100,
      align: 'center',
    },
    { id: 'bank_name', label: t('app.bank'), minWidth: 100, align: 'center' },
    {
      id: 'total_pay_amount',
      label: t('app.totalAmountThb'),
      minWidth: 100,
      align: 'center',
    },
  ];

  const TableModal = [
    {
      id: 'invoice_no',
      label: t('app.invoiceNo'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'sv_period_start_date',
      label: t('app.startDate'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'sv_period_end_date',
      label: t('app.endDate'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'pay_amount',
      label: t('app.invoicePaymentAmountThb'),
      minWidth: 140,
      align: 'right',
    },
  ];

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('transaction_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const exportLock = React.useRef(false);
  const [total, setTotal] = useState(0); // นำ total กลับมาใช้งาน
  const [dataRows, setDataRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const [dataModal, setDataModal] = useState('');
  const merchant = useSelector((state) => state.merchant.value);

  const [openDrawer, setOpenDrawer] = useState(false);

  const handleClose = () => {
    setOpenDrawer(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0); 
  };

  const { data: paymentTransaction, isLoading, isFetching } = useTransactionForPaymentQuery(
    {
      size: rowsPerPage,
      page: page + 1,
      sorgcode: merchant || '',
      sortBy,
      sortOrder,
      search: searchAll,
    },
  );

  const handleSearch = (event) => {
    event.preventDefault();
    setSearchAll(searchInput.trim());
    setPage(0);
  };

  const handleExport = async () => {
    if (exportLock.current || isFetching || !total) return;
    exportLock.current = true;
    setIsExporting(true);
    setExportError('');
    try {
      const blob = await exportBillPayments({
        sorgcode: merchant || '', search: searchAll, sortBy, sortOrder,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bill-payment-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(t('app.billPaymentExportFailed'));
    } finally {
      exportLock.current = false;
      setIsExporting(false);
    }
  };

  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  useEffect(() => {
    if (paymentTransaction) {
      setDataRows(paymentTransaction?.data || []);

      const apiTotal = paymentTransaction?.pagination?.total || 0;
      setTotal(apiTotal);
    }
  }, [paymentTransaction]);

  return (
    <div>
      <div className="">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-lg font-semibold">Bill payment</div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Paper
              component="form"
              onSubmit={handleSearch}
              sx={{
                p: '2px 4px',
                width: { xs: '100%', sm: 320 },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InputBase
                className="app-search-input"
                type="text"
                sx={{ ml: 1, flex: 1, minWidth: 0 }}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                placeholder="ค้นหา"
              />

              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />

              <IconButton type="submit" aria-label="search">
                <SearchIcon sx={{ fontSize: '24px' }} color="primary" />
              </IconButton>
            </Paper>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={isExporting || isFetching || !total}
              onClick={handleExport}
              sx={{ whiteSpace: 'nowrap', minHeight: 44 }}
            >
              {isExporting ? t('app.billPaymentExporting') : t('app.exportAllExcel', { count: total })}
            </Button>
          </div>
        </div>
      </div>
      {exportError && <Alert severity="error" onClose={() => setExportError('')} sx={{ mt: 2 }}>{exportError}</Alert>}
      <div className="mt-2">
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader aria-label="Bill payments">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <div style={{ whiteSpace: 'nowrap', padding: 5 }}>
                        <SortableTableLabel
                          column={column}
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleRequestSort}
                          color="white"
                        />
                      </div>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              {_.size(dataRows) ? (
                <TableBody>
                  {dataRows.length > 0 &&
                    _.map(dataRows, (item, index) => (
                      <StyledTableRow
                        onClick={() => {
                          setOpenDrawer(true);
                          setDataModal(item);
                        }}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={item._id || index}
                      >
                        {columns.map((column) => {
                          const value = item[column.id];

                          if (column.id === 'total_pay_amount') {
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {addCommas(value)}
                              </TableCell>
                            );
                          }

                          if (column.id === 'payment_type') {
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {value}
                              </TableCell>
                            );
                          }

                          if (column.id === 'payment_transaction_no') {
                            return (
                              <TableCell
                                sx={{
                                  color: 'primary.light',
                                  fontWeight: 'bold',
                                }}
                                key={column.id}
                                align={column.align}
                              >
                                {value}
                              </TableCell>
                            );
                          }

                          if (column.id === 'bank_name') {
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {value}
                              </TableCell>
                            );
                          }

                          if (column.id === 'transaction_date') {
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {value
                                  ? dayjs(value).format('DD MMMM YYYY')
                                  : '-'}
                              </TableCell>
                            );
                          }

                          if (column.id === 'payer') {
                            const payerLabel = value === 'OPS System'
                              ? 'OPS System'
                              : value ? 'e-Billing Payment' : '-';
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {payerLabel}
                              </TableCell>
                            );
                          }

                          if (column.id === 'customer_name') {
                            return (
                              <TableCell align={column.align} key={column.id}>
                                {value ? value : '-'}
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell align={column.align} key={column.id}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                    ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <div>ไม่มีข้อมูล</div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={total} // ใช้ข้อมูล total จำนวนทั้งหมดที่มาจาก Backend
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
      <div>
        <Modal
          open={openDrawer}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              handleClose(event, reason);
            }
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Grid item xs={4} sx={{ textAlign: 'right' }}>
              <div>
                <IconButton
                  aria-label="close"
                  size="small"
                  onClick={handleClose}
                  sx={{
                    border: '3px',
                    borderRadius: '50%',
                    borderStyle: 'solid',
                    borderColor: (theme) => theme.palette.grey[900],
                  }}
                >
                  <CloseIcon
                    fontSize="inherit"
                    sx={{
                      color: (theme) => theme.palette.grey[900],
                      strokeWidth: 1.5,
                      stroke: 'currentcolor',
                    }}
                  />
                </IconButton>
              </div>
            </Grid>
            <div
              id="modal-modal-title"
              variant="h6"
              component="h2"
              className=" mt-2 flex"
            >
              {' '}
              {t('app.transactionRefNo')}:
              <div className="font-bold text-green-600 ml-2">
                {' '}
                {dataModal.payment_transaction_no}
              </div>
            </div>

            <div className="mt-5">
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>

                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        {TableModal.map((column) => (
                          <StyledTableCell
                            key={column.id}
                            align={column.align}
                            style={{ minWidth: column.minWidth }}
                          >
                            {column.label}
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    {_.size(dataModal.transaction_detail) ? (
                      <TableBody>
                        {dataModal.transaction_detail.length > 0 &&
                          _.map(dataModal.transaction_detail, (item, index) => (
                            <StyledTableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={item._id || index}
                            >
                              {TableModal.map((column) => {
                                const value = item[column.id];

                                if (column.id === 'pay_amount') {
                                  const amount = Number(value);
                                  const hasAmount = value !== null && value !== undefined
                                    && String(value).trim() !== '' && Number.isFinite(amount);
                                  return (
                                    <TableCell key={column.id} align={column.align} sx={{ whiteSpace: 'nowrap' }}>
                                      {hasAmount ? amount.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }) : '-'}
                                    </TableCell>
                                  );
                                }

                                if (column.id === 'invoice_no') {
                                  return (
                                    <TableCell
                                      key={column.id}
                                      align={column.align}
                                      sx={{ color: 'primary.light' }}
                                    >
                                      <div className="font-bold"> {value} </div>
                                    </TableCell>
                                  );
                                }

                                if (column.id === 'sv_period_start_date') {
                                  return (
                                    <TableCell
                                      align={column.align}
                                      key={column.id}
                                    >
                                      {value
                                        ? dayjs(value).format('YYYY MMMM DD')
                                        : '-'}
                                    </TableCell>
                                  );
                                }
                                if (column.id === 'sv_period_end_date') {
                                  return (
                                    <TableCell
                                      align={column.align}
                                      key={column.id}
                                    >
                                      {value
                                        ? dayjs(value).format('YYYY MMMM DD')
                                        : '-'}
                                    </TableCell>
                                  );
                                }
                                return (
                                  <TableCell
                                    align={column.align}
                                    key={column.id}
                                  >
                                    {value}
                                  </TableCell>
                                );
                              })}
                            </StyledTableRow>
                          ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={TableModal.length} align="center">
                            <div>ไม่มีข้อมูล</div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Paper>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
}
export default BillPayment;
